import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { portalService } from '../../services/api';

interface HazardConfig {
    tenantName: string;
    propertyAddress: string | null;
    hazardTypes: string[];
    emergencyHazardTypes: string[];
    emergencyPrompts: Record<string, string>;
    maxPhotos: number;
    expiresAt: string;
}

interface ReportResult {
    caseRef: string;
    classification: string;
    dayZero: string;
    hazardType?: string;
    duplicateOf?: string | null;
}

export default function HazardReport() {
    const { token } = useParams<{ token: string }>();
    const [config, setConfig] = useState<HazardConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [hazardType, setHazardType] = useState('');
    const [location, setLocation] = useState('');
    const [description, setDescription] = useState('');
    const [photoKeys, setPhotoKeys] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<ReportResult | null>(null);

    useEffect(() => {
        const load = async () => {
            if (!token) {
                setError('Invalid link.');
                setLoading(false);
                return;
            }
            try {
                const res = await portalService.getHazardIntakeConfig(token);
                if (res.data.success) {
                    setConfig(res.data.data);
                    setHazardType(res.data.data.hazardTypes[0] || '');
                } else {
                    setError('This hazard report link is not valid.');
                }
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to load this link. Please contact your landlord.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [token]);

    const isEmergency = (t: string) => !!config?.emergencyHazardTypes.includes(t);
    const prompt = config?.emergencyPrompts?.[hazardType] || null;

    const handlePhotos = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!token || !config || !e.target.files) return;
        const files = Array.from(e.target.files).slice(0, config.maxPhotos - photoKeys.length);
        if (files.length === 0) return;

        setUploading(true);
        try {
            const presign = await portalService.presignHazardPhotos(token, files.length);
            const uploads: { key: string; uploadUrl: string }[] = presign.data.data.uploads;
            const keys: string[] = [];
            for (let i = 0; i < files.length; i++) {
                const blob = new Blob([files[i]], { type: files[i].type || 'image/jpeg' });
                const put = await fetch(uploads[i].uploadUrl, { method: 'PUT', body: blob });
                if (!put.ok) throw new Error(`Photo upload ${i + 1} failed`);
                keys.push(uploads[i].key);
            }
            setPhotoKeys((prev) => [...prev, ...keys].slice(0, config.maxPhotos));
        } catch (err: any) {
            alert(err?.message || 'Failed to upload photos.');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;
        if (!description.trim()) {
            alert('Please describe the problem.');
            return;
        }
        if (isEmergency(hazardType) && !window.confirm('This looks urgent. Are you safe right now?')) {
            return;
        }
        setSubmitting(true);
        try {
            const res = await portalService.submitHazardReport(token, {
                hazardType,
                location,
                description,
                photoKeys,
                reportedAsEmergency: isEmergency(hazardType),
            });
            if (res.data.success) {
                setResult(res.data.data);
            }
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to submit your report. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-indigo-100 to-white flex items-center justify-center">
                <div className="text-indigo-600">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-indigo-100 to-white flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
                    <div className="text-3xl mb-3">&#9888;</div>
                    <p className="text-gray-700">{error}</p>
                </div>
            </div>
        );
    }

    if (result) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-indigo-100 to-white py-8 px-4">
                <div className="max-w-lg mx-auto bg-white rounded-lg shadow p-8">
                    <div className="text-center text-emerald-600 text-4xl mb-3">&#10003;</div>
                    <h1 className="text-xl font-semibold text-gray-900 text-center mb-1">Report received</h1>
                    <p className="text-center text-gray-500 text-sm mb-6">
                        Thank you, {config?.tenantName}. We will acknowledge your report within 5 working days.
                    </p>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4 text-center">
                        <div className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Your case reference</div>
                        <div className="text-2xl font-bold text-indigo-700 mt-1">{result.caseRef}</div>
                    </div>
                    <div className="mt-6 space-y-3 text-sm text-gray-700">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Type</span>
                            <span className="font-medium">{result.hazardType?.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Classification</span>
                            <span className="font-medium">{result.classification}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Received</span>
                            <span className="font-medium">{new Date(result.dayZero).toLocaleString()}</span>
                        </div>
                        {result.duplicateOf && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Linked to existing case</span>
                                <span className="font-medium">Yes</span>
                            </div>
                        )}
                    </div>
                    <div className="mt-6 text-center text-gray-400 text-sm">
                        &copy; 2026 Landlord Pro Compliance Systems
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-indigo-100 to-white py-8 px-4">
            <div className="max-w-lg mx-auto bg-white rounded-lg shadow p-6">
                <h1 className="text-xl font-semibold text-gray-900">Report a hazard</h1>
                <p className="text-sm text-gray-500 mt-1">
                    {config?.propertyAddress
                        ? `For ${config?.tenantName} at ${config.propertyAddress}.`
                        : `For ${config?.tenantName}.`}
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">What is the problem?</label>
                        <select
                            value={hazardType}
                            onChange={(e) => setHazardType(e.target.value)}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2.5 px-3 text-sm"
                        >
                            {config?.hazardTypes.map((t) => (
                                <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                            ))}
                        </select>
                    </div>

                    {prompt && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                            <span className="font-semibold">Important: </span>
                            {prompt}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Where is the problem?</label>
                        <input
                            type="text"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            placeholder="e.g. Kitchen ceiling, hallway"
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2.5 px-3 text-sm"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Describe the problem</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            placeholder="Tell us what you have noticed and how long it has been going on."
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2.5 px-3 text-sm"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Photos ({photoKeys.length}/{config?.maxPhotos})
                        </label>
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handlePhotos}
                            disabled={uploading || photoKeys.length >= (config?.maxPhotos || 5)}
                            className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                        />
                        {uploading && <p className="mt-1 text-xs text-indigo-600">Uploading photos...</p>}
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                            submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                    >
                        {submitting ? 'Submitting...' : 'Submit report'}
                    </button>
                    <p className="text-xs text-gray-400 text-center">
                        Reports are treated as received immediately upon submission.
                    </p>
                </form>
            </div>
        </div>
    );
}
